import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const EditContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const server_url = process.env.REACT_APP_API_URI;
  const editorRefs = {}; // to store editor instances

  useEffect(() => {
    const fetchcontent = async () => {
      try {
        const response = await axios.get(
          `${server_url}/content/get-content/${id}`,
        );
        setContent(response.data.data);
      } catch (err) {
        console.error('Fetch error:', err);
        setError('Failed to fetch content.');
      } finally {
        setLoading(false);
      }
    };
    fetchcontent();
  }, [id, server_url]);

  useEffect(() => {
    const loadCKEditor = () => {
      if (!window.DecoupledEditor) {
        const script = document.createElement('script');
        script.src =
          'https://cdn.ckeditor.com/ckeditor5/41.4.2/decoupled-document/ckeditor.js';
        script.onload = initializeEditors;
        document.body.appendChild(script);
      } else {
        initializeEditors();
      }
    };

    const initializeEditors = () => {
      content?.sections.forEach((section, sectionIndex) => {
        section.contents.forEach(async (contentItem, contentIndex) => {
          const editorId = `editor-${sectionIndex}-${contentIndex}`;
          const editorDiv = document.getElementById(editorId);

          if (editorDiv && !editorDiv.hasAttribute('data-initialized')) {
            editorDiv.setAttribute('data-initialized', 'true');
            const toolbarContainer = document.getElementById(
              `${editorId}-toolbar`,
            );

            try {
              const editor = await window.DecoupledEditor.create(editorDiv);
              toolbarContainer.appendChild(editor.ui.view.toolbar.element);
              editor.setData(contentItem.content || '');
              editorRefs[editorId] = editor;
            } catch (error) {
              console.error('CKEditor init error:', error);
            }
          }
        });
      });
    };

    if (content) {
      loadCKEditor();
    }
  }, [content]);

  const handleTitleChange = (e) => {
    setContent({ ...content, mainTitle: e.target.value });
  };

  const handleSectionTitleChange = (index, e) => {
    const updatedSections = [...content.sections];
    updatedSections[index].title = e.target.value;
    setContent({ ...content, sections: updatedSections });
  };

  const handleSave = async () => {
    setSaving(true);

    const updatedSections = content.sections.map((section, sectionIndex) => ({
      ...section,
      contents: section.contents.map((content, contentIndex) => {
        const editorId = `editor-${sectionIndex}-${contentIndex}`;
        const editorInstance = editorRefs[editorId];
        return {
          ...content,
          content: editorInstance?.getData() || '',
        };
      }),
    }));

    try {
      await axios.put(`${server_url}/content/edit-content/${id}`, {
        id,
        mainTitle: content.mainTitle,
        sections: updatedSections,
      });

      alert('Content updated successfully!');
      navigate('/admin/content');
    } catch (err) {
      console.error('Update error:', err);
      setError('Failed to update content.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSection = () => {
    const newSection = {
      title: '',
      contents: [{ content: '' }],
    };
    setContent({ ...content, sections: [...content.sections, newSection] });
  };

  const handleRemoveSection = (index) => {
    const updatedSections = content.sections.filter((_, i) => i !== index);
    setContent({ ...content, sections: updatedSections });
  };

  const handleAddContent = (sectionIndex) => {
    const updatedSections = [...content.sections];
    updatedSections[sectionIndex].contents.push({ content: '' });
    setContent({ ...content, sections: updatedSections });
  };

  const handleRemoveContent = (sectionIndex, contentIndex) => {
    const updatedSections = [...content.sections];
    updatedSections[sectionIndex].contents = updatedSections[
      sectionIndex
    ].contents.filter((_, i) => i !== contentIndex);
    setContent({ ...content, sections: updatedSections });
  };

  if (loading) return <p>Loading content...</p>;
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Edit Content</h2>

      <input
        type="text"
        value={content.mainTitle}
        onChange={handleTitleChange}
        className="w-full p-2 mb-4 border border-gray-300 rounded"
        placeholder="Main Title"
      />

      {content.sections.map((section, sectionIndex) => (
        <div
          key={section._id || sectionIndex}
          className="mb-6 border p-4 rounded shadow"
        >
          <input
            type="text"
            value={section.title}
            onChange={(e) => handleSectionTitleChange(sectionIndex, e)}
            className="w-full p-2 border border-gray-300 rounded font-semibold"
            placeholder="Section Title"
          />

          {section.contents.map((contentItem, contentIndex) => {
            const editorId = `editor-${sectionIndex}-${contentIndex}`;
            return (
              <div key={contentItem._id || contentIndex} className="mb-12">
                <div id={`${editorId}-toolbar`} className="mb-2 border-b"></div>
                <div
                  id={editorId}
                  className="editor border border-gray-300 p-2 rounded min-h-[200px] bg-white"
                ></div>
                <button
                  onClick={() =>
                    handleRemoveContent(sectionIndex, contentIndex)
                  }
                  className="w-auto mt-2 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-700 float-right"
                >
                  Remove Content
                </button>
              </div>
            );
          })}

          <div className="justify-between items-center mt-2">
            <button
              onClick={() => handleAddContent(sectionIndex)}
              className="px-3 py-1 mr-2 bg-green-500 text-white rounded hover:bg-green-700"
            >
              Add Content
            </button>

            <button
              onClick={() => handleRemoveSection(sectionIndex)}
              className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-700"
            >
              Remove Section
            </button>
          </div>
        </div>
      ))}

      <button
        onClick={handleAddSection}
        className="w-auto px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-700"
      >
        Add Section
      </button>

      <button
        onClick={handleSave}
        className="w-auto m-3 px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-700"
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  );
};

export default EditContent;
